import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import classNames from 'classnames';
import styles from './index.module.scss';
import Layout, { IMAGES_PATH } from '../layout';
import BrandButton from '../brand-button';

const isCyrillicLabel = (label) => /^[а-яёіїєґ]/i.test(label.trim());

const compareBrandLabels = (a, b) => {
  const aCyrillic = isCyrillicLabel(a.label);
  const bCyrillic = isCyrillicLabel(b.label);

  if (aCyrillic !== bCyrillic) return aCyrillic ? 1 : -1;

  return a.label.localeCompare(b.label, aCyrillic ? 'uk' : 'en');
};

const getYesNoOptions = (yes, no) => [
  { value: true, label: yes },
  { value: false, label: no }
];

const PURCHASE_LOCATIONS = [
  'Кіоск',
  'Супермаркет',
  'Магазин біля дому',
  'Онлайн'
].map((label) => ({ value: label, label }));

const SOURCES = [
  'Від представника бренду',
  'Від продавця',
  'Побачив(ла) на касі',
  'Від друзів / знайомих',
  'У соцмережах / месенджерах',
  'В інтернеті (онлайн-магазини)'
].map((label) => ({ value: label, label }));

// Значення мають збігатися з enum бекенду для поля gender.
const GENDERS = ['Чоловік', 'Жінка', 'Інше / не хочу вказувати'].map(
  (label) => ({ value: label, label })
);

const AGES = ['18-24', '25-34', '35-44', '45+'].map((label) => ({
  value: label,
  label
}));

// Тексти без номерів: нумерація рахується від порядку показу, бо частина
// питань за матрицею пропускається, і жорсткі номери з вимог давали б у формі
// дірки (після "1" одразу "11"). Ключі збігаються з полями запиту.
const QUESTION_TITLES = {
  purchase: 'Чи купував(ла) нещодавно продукт Marlboro?',
  feedbackBrand: 'Хочу залишити відгук на:',
  purchaseLocation: 'Вкажи місце, де ти купував(ла) продукт?',
  source: 'Як ти дізнався(лась) про продукт?',
  design: 'Як би ти оцінив(ла) дизайн пачки?',
  quality: 'Як би ти оцінив(ла) смакові властивості?',
  comment: 'Що тобі найбільше сподобалось/не сподобалось у продукті?*',
  repurchaseIntent: 'Чи будеш купувати продукт і надалі?',
  qrSeen: 'Чи бачив(ла) контент за QR кодом всередині пачки?',
  qrcontentScore: 'Як би ти оцінив(ла) контент за QR кодом всередині пачки?',
  gender: 'Вкажи, будь ласка, свою стать',
  age: 'Вкажи, будь ласка, свій вік'
};

const DEFAULT_RATING = 3;
// В базі comment — varchar(300).
const COMMENT_MAX_LENGTH = 300;

const REQUIRED_ERROR = 'Обовʼязкове поле';
const FORBIDDEN_CHARS_ERROR = 'Введено недопустимі символи';

// Дозволені символи для Q7 за вимогами: літери будь-якого алфавіту, цифри,
// пробіли та базова пунктуація. Усе інше (<, >, {, }, html/js/sql) —
// заборонено, і замість тихого обрізання показуємо помилку.
const ALLOWED_COMMENT_REGEX = /^[\p{L}\p{N}\s.,!?:;()'’\-\n]*$/u;

const QuestionCard = ({ title, children, error }) => (
  <div className={styles.cardContainer}>
    <div className={styles.card}>
      <p className={styles.cardTitle}>{title}</p>
      {children}
    </div>
    {error && <p className={styles.errorText}>{error}</p>}
  </div>
);

const RadioGroup = ({ name, options, value, onChange }) => (
  <div className={styles.radioGroup}>
    {options.map((option) => (
      <label className={styles.radioItem} key={String(option.value)}>
        <input
          type="radio"
          name={name}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        <span className={styles.radioMark} />
        <span className={styles.radioLabel}>{option.label}</span>
      </label>
    ))}
  </div>
);

const toggleValue = (values, value) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

const CheckboxGroup = ({ name, options, values, onChange }) => (
  <div className={styles.radioGroup}>
    {options.map((option) => (
      <label
        className={classNames(styles.radioItem, styles.checkboxItem)}
        key={String(option.value)}
      >
        <input
          type="checkbox"
          name={name}
          checked={values.includes(option.value)}
          onChange={() => onChange(toggleValue(values, option.value))}
        />
        <span className={styles.radioMark} />
        <span className={styles.radioLabel}>{option.label}</span>
      </label>
    ))}
  </div>
);

const RatingRange = ({ value, onChange }) => (
  <div className={styles.rating}>
    <Range
      step={1}
      min={1}
      max={5}
      values={[value]}
      onChange={(values) => onChange(values[0])}
      renderTrack={({ props, children }) => (
        <div
          {...props}
          style={{
            ...props.style,
            display: 'flex',
            position: 'relative',
            height: '22px',
            width: '100%',
            borderRadius: '4px',
            background: getTrackBackground({
              values: [value],
              colors: ['#580001', '#ac3133'],
              min: 1,
              max: 5
            })
          }}
        >
          {children}
        </div>
      )}
      renderThumb={({ props }) => <div {...props} className={styles.thumb} />}
    />
    <div className={styles.ratingScale}>
      {[1, 2, 3, 4, 5].map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>
  </div>
);

const SelectField = ({ placeholder, options, value, onChange, disabled }) => (
  <div className={styles.selectWrapper}>
    <select
      className={styles.select}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const QuestionnaireScreen = ({
  brands = [],
  skuNames = [],
  submitError,
  onSubmit
}) => {
  const [purchase, setPurchase] = useState(null);
  const [feedbackBrand, setFeedbackBrand] = useState('');
  const [purchaseLocation, setPurchaseLocation] = useState([]);
  const [source, setSource] = useState([]);
  const [design, setDesign] = useState(DEFAULT_RATING);
  const [quality, setQuality] = useState(DEFAULT_RATING);
  const [comment, setComment] = useState('');
  const [repurchaseIntent, setRepurchaseIntent] = useState(null);
  const [qrSeen, setQrSeen] = useState(null);
  const [qrcontentScore, setQrcontentScore] = useState(DEFAULT_RATING);
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [brand, setBrand] = useState('');
  const [format, setFormat] = useState('');

  const [commentError, setCommentError] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Після успішного відгуку флоу перемикає екран і цей уже розмонтований —
  // без цієї перевірки React лається на setState після unmount.
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Матриця переходів із вимог. Наступне питання з'являється лише після
  // відповіді на попереднє:
  //   Q1 "Ні" -> Q11, Q2 обрано -> Q3, Q3..Q7 -> наступне, Q8 будь-яка -> Q9,
  //   Q9 "Так" -> Q10, Q9 "Ні" -> Q11, Q10 -> Q11 -> Q12.
  // Q5, Q6 і Q10 — повзунки, які мають значення з самого початку, тому
  // вважаються відповіданими одразу, щойно з'явились.
  //
  // Q2 тут — не "чи купував" (Так/Ні), а випадаючий список продуктів кампанії
  // (sku_name з GET sku-name). Відповіді "Ні" в ньому не існує, тому блок
  // Q3-Q10 відкриває сам факт вибору продукту.
  const isPurchaseBranch = purchase === true && !!feedbackBrand;

  const isFeedbackBrandVisible = purchase === true;
  const isPurchaseLocationVisible = isPurchaseBranch;
  const isSourceVisible =
    isPurchaseLocationVisible && purchaseLocation.length > 0;
  const isDesignVisible = isSourceVisible && source.length > 0;
  const isQualityVisible = isDesignVisible;
  const isCommentVisible = isQualityVisible;
  const isRepurchaseIntentVisible =
    isCommentVisible && !!comment.trim() && !commentError;
  const isQrSeenVisible =
    isRepurchaseIntentVisible && repurchaseIntent !== null;
  const isQrScoreVisible = isQrSeenVisible && qrSeen === true;
  const isGenderVisible =
    purchase === false || (isQrSeenVisible && qrSeen !== null);
  const isAgeVisible = isGenderVisible && !!gender;
  // Випадаючі списки в макеті стоять під останнім питанням, тому й показуємо
  // їх останніми — коли вся анкета вже пройдена.
  const isBrandVisible = isPurchaseBranch && isAgeVisible && !!age;

  // Видимі питання в порядку показу — з цього списку береться номер у заголовку,
  // тому нумерація завжди суцільна: 1, 2, 3... незалежно від пропущених гілок.
  const visibleQuestions = [
    'purchase',
    ...(isFeedbackBrandVisible ? ['feedbackBrand'] : []),
    ...(isPurchaseLocationVisible ? ['purchaseLocation'] : []),
    ...(isSourceVisible ? ['source'] : []),
    ...(isDesignVisible ? ['design'] : []),
    ...(isQualityVisible ? ['quality'] : []),
    ...(isCommentVisible ? ['comment'] : []),
    ...(isRepurchaseIntentVisible ? ['repurchaseIntent'] : []),
    ...(isQrSeenVisible ? ['qrSeen'] : []),
    ...(isQrScoreVisible ? ['qrcontentScore'] : []),
    ...(isGenderVisible ? ['gender'] : []),
    ...(isAgeVisible ? ['age'] : [])
  ];

  const getTitle = (key) =>
    `${visibleQuestions.indexOf(key) + 1}. ${QUESTION_TITLES[key]}`;

  const skuNameOptions = useMemo(
    () => skuNames.map((name) => ({ value: name, label: name })),
    [skuNames]
  );

  const brandOptions = useMemo(
    () =>
      brands
        .map(({ label }) => ({ value: label, label }))
        .sort(compareBrandLabels),
    [brands]
  );

  const formatOptions = useMemo(() => {
    const selected = brands.find(({ label }) => label === brand);

    return (selected?.children || []).map(({ label }) => ({
      value: label,
      label
    }));
  }, [brands, brand]);

  // Кнопка "Залишити відгук" з'являється після останнього питання шляху —
  // тобто коли заповнені всі видимі обов'язкові поля.
  const isPathCompleted = Boolean(
    purchase !== null &&
    (purchase === false || !!feedbackBrand) &&
    (!isPurchaseBranch ||
      (purchaseLocation.length &&
        source.length &&
        comment.trim() &&
        !commentError &&
        repurchaseIntent !== null &&
        qrSeen !== null &&
        brand &&
        format)) &&
    gender &&
    age
  );

  const handleCommentChange = (value) => {
    if (!ALLOWED_COMMENT_REGEX.test(value)) {
      setCommentError(FORBIDDEN_CHARS_ERROR);
      return;
    }

    setCommentError('');
    setComment(value.slice(0, COMMENT_MAX_LENGTH));
  };

  // Дані прихованих питань не зберігаються в результатах опитування, тому
  // при зміні відповіді на Q1/Q2 очищаємо весь залежний блок Q3-Q10.
  const resetPurchaseBranch = () => {
    setPurchaseLocation([]);
    setSource([]);
    setDesign(DEFAULT_RATING);
    setQuality(DEFAULT_RATING);
    setComment('');
    setCommentError('');
    setRepurchaseIntent(null);
    setQrSeen(null);
    setQrcontentScore(DEFAULT_RATING);
    setBrand('');
    setFormat('');
  };

  const handlePurchaseChange = (value) => {
    setPurchase(value);

    if (value === false) {
      setFeedbackBrand('');
      resetPurchaseBranch();
    }
  };

  // Поки продукт обрано — Q3-Q10 на екрані, тому перемикання між продуктами
  // відповіді не чистить; чистимо лише коли список повернули в плейсхолдер і
  // блок ховається.
  const handleFeedbackBrandChange = (value) => {
    setFeedbackBrand(value);

    if (!value) resetPurchaseBranch();
  };

  const handleBrandChange = (value) => {
    setBrand(value);
    setFormat('');
  };

  const handleSubmit = async () => {
    setIsValidated(true);

    if (!isPathCompleted) return;

    const purchaseAnswers = isPurchaseBranch
      ? {
          purchaseLocation,
          source,
          design,
          quality,
          comment: comment.trim(),
          repurchaseIntent,
          qrSeen,
          ...(isQrScoreVisible ? { qrcontentScore } : {}),
          brand,
          format
        }
      : {};

    try {
      setIsSubmitting(true);
      await onSubmit({
        purchase,
        productSeen: purchase,
        ...(purchase === true ? { feedbackBrand } : {}),
        ...purchaseAnswers,
        gender,
        age
      });
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const getRequiredError = (isEmpty) =>
    isValidated && isEmpty ? REQUIRED_ERROR : '';

  return (
    <Layout contentClassName={styles.content}>
      <p className={styles.title}>
        Нам дуже важливий твій відгук, щоб робити продукт кращим 💜
      </p>
      <p className={styles.subtitle}>
        Тож проходь опитування та поділись будь ласка думками щодо продукту ⬇
      </p>
      <img
        className={styles.packs}
        src={`${IMAGES_PATH}/packs.png`}
        alt="Marlboro Red, Gold, Touch, Fine Touch"
      />
      <div className={styles.cardList}>
        <QuestionCard
          title={getTitle('purchase')}
          error={getRequiredError(purchase === null)}
        >
          <RadioGroup
            name="purchase"
            options={getYesNoOptions('Так, купував(ла)', 'Ні, не купував(ла)')}
            value={purchase}
            onChange={handlePurchaseChange}
          />
        </QuestionCard>
        {isFeedbackBrandVisible && (
          <QuestionCard
            title={getTitle('feedbackBrand')}
            error={getRequiredError(!feedbackBrand)}
          >
            <SelectField
              placeholder="Обери продукт"
              options={skuNameOptions}
              value={feedbackBrand}
              onChange={handleFeedbackBrandChange}
              disabled={!skuNameOptions.length}
            />
          </QuestionCard>
        )}
        {isPurchaseLocationVisible && (
          <QuestionCard
            title={getTitle('purchaseLocation')}
            error={getRequiredError(!purchaseLocation.length)}
          >
            <CheckboxGroup
              name="purchaseLocation"
              options={PURCHASE_LOCATIONS}
              values={purchaseLocation}
              onChange={setPurchaseLocation}
            />
          </QuestionCard>
        )}
        {isSourceVisible && (
          <QuestionCard
            title={getTitle('source')}
            error={getRequiredError(!source.length)}
          >
            <CheckboxGroup
              name="source"
              options={SOURCES}
              values={source}
              onChange={setSource}
            />
          </QuestionCard>
        )}
        {isDesignVisible && (
          <QuestionCard title={getTitle('design')}>
            <RatingRange value={design} onChange={setDesign} />
          </QuestionCard>
        )}
        {isQualityVisible && (
          <QuestionCard title={getTitle('quality')}>
            <RatingRange value={quality} onChange={setQuality} />
          </QuestionCard>
        )}
        {isCommentVisible && (
          <QuestionCard
            title={getTitle('comment')}
            error={commentError || getRequiredError(!comment.trim())}
          >
            <textarea
              className={classNames(
                styles.textarea,
                (commentError || (isValidated && !comment.trim())) &&
                  styles.textareaError
              )}
              value={comment}
              maxLength={COMMENT_MAX_LENGTH}
              onChange={(event) => handleCommentChange(event.target.value)}
            />
          </QuestionCard>
        )}
        {isRepurchaseIntentVisible && (
          <QuestionCard
            title={getTitle('repurchaseIntent')}
            error={getRequiredError(repurchaseIntent === null)}
          >
            <RadioGroup
              name="repurchaseIntent"
              options={getYesNoOptions('Так, буду', 'Ні, не буду')}
              value={repurchaseIntent}
              onChange={setRepurchaseIntent}
            />
          </QuestionCard>
        )}
        {isQrSeenVisible && (
          <QuestionCard
            title={getTitle('qrSeen')}
            error={getRequiredError(qrSeen === null)}
          >
            <RadioGroup
              name="qrSeen"
              options={getYesNoOptions('Так, бачив(ла)', 'Ні, ще не бачив(ла)')}
              value={qrSeen}
              onChange={setQrSeen}
            />
          </QuestionCard>
        )}
        {isQrScoreVisible && (
          <QuestionCard title={getTitle('qrcontentScore')}>
            <RatingRange value={qrcontentScore} onChange={setQrcontentScore} />
          </QuestionCard>
        )}
        {isGenderVisible && (
          <QuestionCard
            title={getTitle('gender')}
            error={getRequiredError(!gender)}
          >
            <RadioGroup
              name="gender"
              options={GENDERS}
              value={gender}
              onChange={setGender}
            />
          </QuestionCard>
        )}
        {isAgeVisible && (
          <QuestionCard title={getTitle('age')} error={getRequiredError(!age)}>
            <RadioGroup
              name="age"
              options={AGES}
              value={age}
              onChange={setAge}
            />
          </QuestionCard>
        )}
      </div>
      {isBrandVisible && (
        <div className={styles.brandContainer}>
          <p className={styles.brandTitle}>
            Окрім Marlboro, якому ще бренду надаєш перевагу?
          </p>
          <SelectField
            placeholder="Обери бренд"
            options={brandOptions}
            value={brand}
            onChange={handleBrandChange}
          />
          {isValidated && !brand && (
            <p className={styles.errorText}>{REQUIRED_ERROR}</p>
          )}
          <p className={styles.brandTitle}>
            Сигарети якого формату ти обираєш?
          </p>
          <SelectField
            placeholder="Обери формат"
            options={formatOptions}
            value={format}
            onChange={setFormat}
            disabled={!formatOptions.length}
          />
          {isValidated && !format && (
            <p className={styles.errorText}>{REQUIRED_ERROR}</p>
          )}
        </div>
      )}
      {/* Після останнього питання показуємо лише кнопку: блок «Чудово!
          Дякуємо...» з макета прибрано за вимогою — подяка є на окремому
          екрані після відправки. */}
      {isPathCompleted && (
        <>
          {submitError && <p className={styles.submitError}>{submitError}</p>}
          <BrandButton
            className={styles.submitButton}
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Залишити відгук 📝
          </BrandButton>
        </>
      )}
    </Layout>
  );
};

export default QuestionnaireScreen;
